import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Heading,
  Divider,
  SimpleGrid,
  useColorModeValue,
  List,
  ListItem,
  InputGroup,
  InputRightElement,
  Text,
  Select,
  useToast,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { SearchIcon, CalendarIcon, ViewIcon, DownloadIcon } from '@chakra-ui/icons';
import { getData } from '../../utils/httpClient.js';
import api from '../../services/api.js';
import PageTable from 'components/layout/PageTable';
import PurchaseInvoiceDetailModal from '../PurchaseInvoice/PurchaseInvoiceDetailModal.js';

export default function AccountStatement() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [accountType, setAccountType] = useState(
    () => searchParams.get('type') === 'receivable' ? 'receivable' : 'payable',
  );
  const [providers, setProviders] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entitySearch, setEntitySearch] = useState('');
  const [filteredEntities, setFilteredEntities] = useState([]);
  const [showEntitySuggestions, setShowEntitySuggestions] = useState(false);
  const [accountData, setAccountData] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [creditNotes, setCreditNotes] = useState(null);
  const [payments, setPayments] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentInvoicePage] = useState(1);
  const [currentCreditNotePage] = useState(1);
  const [currentPaymentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [, setTotalInvoicePages] = useState(0);
  const [, setTotalCreditNotePages] = useState(0);
  const [, setTotalPaymentPages] = useState(0);
  const [pdfCurrency, setPdfCurrency] = useState('UYU');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [purchaseInvoiceDetailId, setPurchaseInvoiceDetailId] = useState(null);

  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    if (accountType === 'receivable') {
      loadClients();
    } else {
      loadProviders();
    }
    resetEntitySelection();
    // Cargar resumen general al cambiar tipo de cuenta
    loadGeneralAccountStatement();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountType]);

  // Cargar resumen general al iniciar el componente
  useEffect(() => {
    loadGeneralAccountStatement(currentPage, rowsPerPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProviders = async () => {
    try {
      const response = await getData('/providers');
      setProviders(response?.payload?.providers || []);
    } catch (error) {
      toast({
        title: 'Error al cargar proveedores',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const loadClients = async () => {
    try {
      const response = await getData('/account-statement/clients?page=1&limit=500');
      const accounts = response?.payload?.accountStatements || [];
      setClients(
        accounts.map((a) => ({
          id: a.invoiceCustomerId,
          name: a.invoiceCustomer?.name,
          documentNumber: a.invoiceCustomer?.documentNumber,
        })),
      );
    } catch (error) {
      toast({
        title: 'Error al cargar clientes',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const resetEntitySelection = () => {
    setSelectedEntity(null);
    setEntitySearch('');
    setAccountData(null);
    setFilteredEntities([]);
    setShowEntitySuggestions(false);
  };

  const handleEntitySearch = (value) => {
    setEntitySearch(value);
    const entities = accountType === 'receivable' ? clients : providers;

    if (value.trim()) {
      const filtered = entities.filter(entity =>
        entity.denominacion?.toLowerCase().includes(value.toLowerCase()) ||
        entity.name?.toLowerCase().includes(value.toLowerCase()) ||
        (entity.rut && entity.rut.toLowerCase().includes(value.toLowerCase())) ||
        (entity.documentNumber && entity.documentNumber.toLowerCase().includes(value.toLowerCase()))
      );
      setFilteredEntities(filtered);
      setShowEntitySuggestions(true);
    } else {
      setShowEntitySuggestions(false);
    }
  };

  const selectEntity = (entity) => {
    setSelectedEntity(entity);
    setEntitySearch(`${entity.name} - ${entity.rut || entity.documentNumber || entity.nro_doc}`);
    setShowEntitySuggestions(false);
    loadAccountStatement(entity.id);
  };

  const downloadAccountStatementPdf = async () => {
    if (!selectedEntity?.id) {
      toast({
        title: 'Seleccioná un cliente o proveedor',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setDownloadingPdf(true);
    try {
      const base =
        accountType === 'receivable'
          ? `/account-statement/clients/${selectedEntity.id}/pdf`
          : `/account-statement/providers/${selectedEntity.id}/pdf`;
      const params = new URLSearchParams();
      if (dateRange.from) params.set('from', dateRange.from);
      if (dateRange.to) params.set('to', dateRange.to);
      params.set('currency', pdfCurrency || 'UYU');
      const url = `${base}?${params.toString()}`;
      const response = await api.get(url, {
        responseType: 'arraybuffer',
        skipGlobalLoader: true,
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      if (!blob.size) throw new Error('PDF vacío');
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      const kind = accountType === 'receivable' ? 'cliente' : 'proveedor';
      link.download = `estado_cuenta_${kind}_${selectedEntity.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      toast({
        title: 'Error al descargar el estado de cuenta',
        description: error?.message || 'No se pudo generar el PDF',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const loadGeneralAccountStatement = async (page = currentPage, invoicePage = currentInvoicePage, creditNotePage = currentCreditNotePage, paymentPage = currentPaymentPage, limit = rowsPerPage) => {
    setLoading(true);
    try {
      let endpoint;
      let invoicesEndpoint;
      let creditNotesEndpoint;
      let paymentsEndpoint;
      if (accountType === 'receivable') {
        endpoint = '/account-statement/clients';
        invoicesEndpoint = '/account-statement/clientsInvoices';
        creditNotesEndpoint = '/account-statement/clientsCreditNotes';
        paymentsEndpoint = '/account-statement/clientsPayments';
      } else {
        endpoint = `/account-statement/providers`;
        invoicesEndpoint = '/account-statement/providersInvoices';
        creditNotesEndpoint = '/account-statement/providersCreditNotes';
        paymentsEndpoint = '/account-statement/providersPayments';
      }
      const dateQuery =
        dateRange.from && dateRange.to
          ? `&from=${dateRange.from}&to=${dateRange.to}`
          : dateRange.from
            ? `&from=${dateRange.from}`
            : dateRange.to
              ? `&to=${dateRange.to}`
              : '';
      const url = `${endpoint}?page=${page}&limit=${limit}${dateQuery}`;
      const urlInvoices = `${invoicesEndpoint}?page=${invoicePage}&limit=${limit}${dateQuery}`;
      const urlCreditNotes = `${creditNotesEndpoint}?page=${creditNotePage}&limit=${limit}${dateQuery}`;
      const urlPayments = `${paymentsEndpoint}?page=${paymentPage}&limit=${limit}${dateQuery}`;
      const response = await getData(url);
      const responseInvoices = await getData(urlInvoices);
      const responseCreditNotes = await getData(urlCreditNotes);
      const responsePayments = await getData(urlPayments);

      setAccountData(response?.payload.accountStatements || null);
      setTotalPages(Math.ceil(response?.payload.total / limit));
      setInvoices(responseInvoices?.payload.invoices || []);
      setTotalInvoicePages(Math.ceil(responseInvoices?.payload.total / limit));
      setCreditNotes(responseCreditNotes?.payload.creditNotes || []);
      setTotalCreditNotePages(Math.ceil(responseCreditNotes?.payload.total / limit));
      setPayments(responsePayments?.payload.payments || []);
      setTotalPaymentPages(Math.ceil(responsePayments?.payload.total / limit));

    } catch (error) {
      toast({
        title: 'Error al cargar resumen general',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setAccountData(null);
    } finally {
      setLoading(false);
    }
  };

  const loadAccountStatement = async (entityId, page = currentPage, invoicePage = currentInvoicePage, creditNotePage = currentCreditNotePage, paymentPage = currentPaymentPage, limit = rowsPerPage) => {
    setLoading(true);
    try {
      let endpoint;
      let invoicesEndpoint;
      let creditNotesEndpoint;
      let paymentsEndpoint;
      if (accountType === 'receivable') {
        endpoint = '/account-statement/clients';
        invoicesEndpoint = '/account-statement/clientsInvoices';
        creditNotesEndpoint = '/account-statement/clientsCreditNotes';
        paymentsEndpoint = '/account-statement/clientsPayments';
      } else {
        endpoint = `/account-statement/providers`;
        invoicesEndpoint = '/account-statement/providersInvoices';
        creditNotesEndpoint = '/account-statement/providersCreditNotes';
        paymentsEndpoint = '/account-statement/providersPayments';
      }

      const dateQuery =
        dateRange.from && dateRange.to
          ? `&from=${dateRange.from}&to=${dateRange.to}`
          : dateRange.from
            ? `&from=${dateRange.from}`
            : dateRange.to
              ? `&to=${dateRange.to}`
              : '';

      const url = `${endpoint}/${entityId}?page=${page}&limit=${limit}${dateQuery}`;
      const urlInvoices = `${invoicesEndpoint}/${entityId}?page=${invoicePage}&limit=${limit}${dateQuery}`;
      const urlCreditNotes = `${creditNotesEndpoint}/${entityId}?page=${creditNotePage}&limit=${limit}${dateQuery}`;
      const urlPayments = `${paymentsEndpoint}/${entityId}?page=${paymentPage}&limit=${limit}${dateQuery}`;
      const response = await getData(url);
      const responseInvoices = await getData(urlInvoices);
      const responseCreditNotes = await getData(urlCreditNotes);
      const responsePayments = await getData(urlPayments);

      setAccountData(response?.payload.accountStatements || null);
      setTotalPages(Math.ceil(response?.payload.total / limit));
      setInvoices(responseInvoices?.payload.invoices || []);
      setTotalInvoicePages(Math.ceil(responseInvoices?.payload.total / limit));
      setCreditNotes(responseCreditNotes?.payload.creditNotes || []);
      setTotalCreditNotePages(Math.ceil(responseCreditNotes?.payload.total / limit));
      setPayments(responsePayments?.payload.payments || []);
      setTotalPaymentPages(Math.ceil(responsePayments?.payload.total / limit));

    } catch (error) {
      toast({
        title: 'Error al cargar estado de cuenta',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setAccountData(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pendiente':
      case 'pending':
        return 'yellow';
      case 'pagada':
      case 'paid':
      case 'pagado':
        return 'green';
      case 'vencida':
      case 'overdue':
        return 'red';
      case 'parcial':
      case 'partial':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-UY');
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  const getDaysOverdue = (dueDate) => {
    if (!dueDate) return 0;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const renderPurchaseInvoiceNumber = (invoice) => {
    const label =
      invoice.invoiceId ||
      (invoice.serie != null && invoice.numero != null
        ? `${invoice.serie}-${invoice.numero}`
        : '—');

    if (accountType === 'payable' && invoice.invoiceId) {
      return (
        <Button
          size="sm"
          variant="link"
          colorScheme="blue"
          fontWeight="medium"
          onClick={() => setPurchaseInvoiceDetailId(invoice.invoiceId)}
        >
          {label}
        </Button>
      );
    }

    return (
      <Text fontWeight="medium" as="span">
        {label}
      </Text>
    );
  };

  const renderPaymentLinkedInvoices = (payment) => {
    if (accountType === 'payable' && payment.providerInvoices?.length) {
      return (
        <VStack align="start" spacing={0}>
          {payment.providerInvoices.map((f) => (
            <Button
              key={f.invoiceId}
              size="xs"
              variant="link"
              colorScheme="blue"
              onClick={() => setPurchaseInvoiceDetailId(f.invoiceId)}
            >
              {f.invoiceId}
            </Button>
          ))}
        </VStack>
      );
    }

    const text =
      payment.providerInvoices
        ?.map((f) => f.invoiceId || f.numero)
        .join(', ') ||
      payment.invoices?.map((f) => `${f.series}-${f.number}`).join(', ') ||
      payment.facturas?.map((f) => f.numero).join(', ');

    return (
      <Text fontSize="xs">{text || '—'}</Text>
    );
  };

  return (
    <PageTable
      flexDirection="column"
      w="100%"
      px={{ base: 3, md: 4 }}
      py={{ base: 3, md: 4 }}
      stickyActions={false}
    >
      <VStack spacing={4} align="stretch">
        {/* Selector de tipo de cuenta */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <FormControl>
            <FormLabel fontSize="sm">Tipo de Cuenta</FormLabel>
            <Select
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
              size="sm"
            >
              <option value="receivable">Cuentas por Cobrar (Clientes)</option>
              <option value="payable">Cuentas por Pagar (Proveedores)</option>
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">Fecha Desde</FormLabel>
            <Input size="sm" type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">Fecha Hasta</FormLabel>
            <Input size="sm" type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
            />
          </FormControl>
        </SimpleGrid>

        {/* Selector de entidad */}
        <FormControl>
          <FormLabel fontSize="sm">
            {accountType === 'receivable' ? 'Buscar Cliente' : 'Buscar Proveedor'}
          </FormLabel>
          <InputGroup>
            <Input
              size="sm"
              value={entitySearch}
              onChange={(e) => handleEntitySearch(e.target.value)}
              placeholder={`Buscar ${accountType === 'receivable' ? 'cliente' : 'proveedor'} por nombre o documento`}
            />
            <InputRightElement>
              <SearchIcon />
            </InputRightElement>
          </InputGroup>

          {showEntitySuggestions && filteredEntities.length > 0 && (
            <Box
              position="relative"
              bg={bgColor}
              border="1px"
              borderColor={borderColor}
              borderRadius="md"
              mt={1}
              maxH="200px"
              overflowY="auto"
              zIndex={10}
            >
              <List>
                {filteredEntities.slice(0, 10).map((entity) => (
                  <ListItem
                    key={entity.id}
                    p={2}
                    cursor="pointer"
                    _hover={{ bg: 'gray.100' }}
                    onClick={() => selectEntity(entity)}
                  >
                    <Text fontSize="sm" fontWeight="medium" color="blue.600">
                      {entity.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {accountType === 'receivable' ? 'CI/RUT:' : 'RUT:'} {entity.rut || entity.documentNumber}
                    </Text>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </FormControl>

        {/* Botones de acción */}
        <HStack spacing={4}>
          <Button
            colorScheme="gray"
            onClick={() => selectedEntity ? loadAccountStatement(selectedEntity.id) : loadGeneralAccountStatement()}
            isLoading={loading}
            loadingText="Cargando..."
            leftIcon={<CalendarIcon />}
          >
            {selectedEntity ? 'Actualizar Estado de Cuenta' : 'Cargar Resumen General'}
          </Button>

          {selectedEntity && (
            <Button
              variant="outline"
              onClick={() => {
                resetEntitySelection();
                loadGeneralAccountStatement();
              }}
            >
              Ver Resumen General
            </Button>
          )}

          {selectedEntity && (
            <Button
              colorScheme="blue"
              variant="solid"
              leftIcon={<DownloadIcon />}
              onClick={downloadAccountStatementPdf}
              isLoading={downloadingPdf}
              loadingText="Generando..."
            >
              Descargar PDF
            </Button>
          )}

          {accountType === 'receivable' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/admin/customer-payments')}
            >
              Registrar recibo
            </Button>
          )}
        </HStack>

        <Divider />

        {/* Resumen de cuenta */}
        {accountData && (
          <VStack spacing={6} align="stretch">
            {/* Tabla de listado general */}
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>{accountType === 'receivable' ? 'Documento' : 'RUT'}</Th>
                    <Th>Nombre</Th>
                    <Th>Debe</Th>
                    <Th>Cobrado / Pagado</Th>
                    <Th>Detalles</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {accountData.map((account) => (
                    <Tr key={account.id}>
                      <Td>
                        <Text fontSize="sm" fontWeight="medium" color="blue.600">
                          {accountType === 'receivable'
                            ? account.invoiceCustomer?.documentNumber
                            : account.provider?.rut}
                        </Text>
                      </Td>
                      <Td>
                        {accountType === 'receivable'
                          ? account.invoiceCustomer?.name
                          : account.provider?.name}
                      </Td>
                      <Td>{account.totalDebt}</Td>
                      <Td>{account.totalPayments}</Td>
                      <Td>
                        <IconButton
                          icon={<ViewIcon />}
                          size="sm"
                          colorScheme="gray"
                          variant="ghost"
                          onClick={() =>
                            selectEntity(
                              accountType === 'receivable'
                                ? {
                                    id: account.invoiceCustomerId,
                                    name: account.invoiceCustomer?.name,
                                    documentNumber:
                                      account.invoiceCustomer?.documentNumber,
                                  }
                                : account.provider,
                            )
                          }
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              {accountData && totalPages > 1 && (
                <HStack justify="center" mt={4}>
                  <Button
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    isDisabled={currentPage === 1}
                  >
                    Anterior
                  </Button>
                  <Text>
                    Página {currentPage} de {totalPages}
                  </Text>
                  <Button
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    isDisabled={currentPage >= totalPages}
                  >
                    Siguiente
                  </Button>
                </HStack>
              )}
            </Box>

            <Divider />

            {/* <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
              <Stat>
                <StatLabel color="secondaryGray.600">Total Facturado</StatLabel>
                <StatNumber color="blue.600">{formatCurrency(accountData.summary?.total_invoiced)}</StatNumber>
                <StatHelpText>Últimos 12 meses</StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel color="secondaryGray.600">Total Pagado</StatLabel>
                <StatNumber color="green.600">{formatCurrency(accountData.summary?.total_paid)}</StatNumber>
                <StatHelpText>Pagos recibidos</StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel color="secondaryGray.600">Saldo Pendiente</StatLabel>
                <StatNumber color="orange.600">{formatCurrency(accountData.summary?.pending_balance)}</StatNumber>
                <StatHelpText>Por cobrar/pagar</StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel color="secondaryGray.600">Vencido</StatLabel>
                <StatNumber color="red.600">{formatCurrency(accountData.summary?.overdue_balance)}</StatNumber>
                <StatHelpText>Facturas vencidas</StatHelpText>
              </Stat>
            </SimpleGrid>

            <Divider /> */}

            {/* Tabs para diferentes vistas */}
            <Tabs
              onChange={(index) => {
                if (index === 1) setPdfCurrency('USD');
                else setPdfCurrency('UYU');
              }}
            >
              <TabList>
                <Tab>Facturas en Pesos (UYU)</Tab>
                <Tab>Facturas en Dólares (USD)</Tab>
                <Tab>Notas de Crédito</Tab>
                <Tab>Historial de Pagos</Tab>
              </TabList>

              <TabPanels>
                {/* Tab de Facturas en Pesos */}
                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    <Heading size="md" color="blue.700">
                      💵 Facturas Pendientes en Pesos Uruguayos (UYU)
                    </Heading>

                    {invoices.filter(inv => inv.currency?.code === 'UYU' || !inv.currency?.code).length > 0 ? (
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>{accountType === 'receivable' ? 'Cliente' : 'Proveedor'}</Th>
                              <Th>Número</Th>
                              <Th>Fecha</Th>
                              <Th>Vencimiento</Th>
                              <Th>Total</Th>
                              <Th>Saldo</Th>
                              <Th>Días Venc.</Th>
                              <Th>Estado</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {invoices
                              .filter(inv => inv.currency?.code === 'UYU' || !inv.currency?.code)
                              .map((invoice) => (
                                <Tr key={invoice.invoiceId}>
                                  <Td>
                                    <VStack align="start" spacing={0}>
                                      <Text fontSize="sm" fontWeight="medium" color="blue.600">
                                        {invoice.provider?.rut || invoice.client?.documentNumber}
                                      </Text>
                                      <Text fontSize="xs" color="gray.500">
                                        {invoice.provider?.name || invoice.client?.name}
                                      </Text>
                                    </VStack>
                                  </Td>
                                  <Td>{renderPurchaseInvoiceNumber(invoice)}</Td>
                                  <Td>{formatDate(invoice.date || invoice.fecha_emison)}</Td>
                                  <Td>{formatDate(invoice.expirationDate)}</Td>
                                  <Td>{formatCurrency(invoice.totalAmount)}</Td>
                                  <Td fontWeight="medium" color="orange.600">
                                    {formatCurrency(
                                      invoice.payment?.paymentAmount 
                                        ? invoice.totalAmount - Number(invoice.payment.paymentAmount) 
                                        : invoice.totalAmount
                                    )}
                                  </Td>
                                  <Td>
                                    {getDaysOverdue(invoice.expirationDate) > 0 && (
                                      <Text color="red.500" fontSize="sm" fontWeight="medium">
                                        {getDaysOverdue(invoice.expirationDate)}
                                      </Text>
                                    )}
                                  </Td>
                                  <Td>
                                    <Badge colorScheme={getStatusColor(invoice.status)}>
                                      {invoice.status}
                                    </Badge>
                                  </Td>
                                </Tr>
                              ))}
                          </Tbody>
                          <Thead>
                            <Tr bg="gray.50">
                              <Th colSpan={4}>Total en UYU</Th>
                              <Th fontWeight="bold" color="blue.700">
                                {formatCurrency(
                                  invoices
                                    .filter(inv => inv.currency?.code === 'UYU' || !inv.currency?.code)
                                    .reduce((sum, inv) => sum + (inv.totalAmount || 0), 0)
                                )}
                              </Th>
                              <Th fontWeight="bold" color="orange.700">
                                {formatCurrency(
                                  invoices
                                    .filter(inv => inv.currency?.code === 'UYU' || !inv.currency?.code)
                                    .reduce((sum, inv) => 
                                      sum + (inv.payment?.paymentAmount 
                                        ? inv.totalAmount - Number(inv.payment.paymentAmount) 
                                        : inv.totalAmount), 0)
                                )}
                              </Th>
                              <Th colSpan={2}></Th>
                            </Tr>
                          </Thead>
                        </Table>
                      </Box>
                    ) : (
                      <Text color="gray.500" textAlign="center">
                        No hay facturas pendientes en pesos
                      </Text>
                    )}
                  </VStack>
                </TabPanel>

                {/* Tab de Facturas en Dólares */}
                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    <Heading size="md" color="green.700">
                      💵 Facturas Pendientes en Dólares (USD)
                    </Heading>

                    {invoices.filter(inv => inv.currency?.code === 'USD').length > 0 ? (
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>{accountType === 'receivable' ? 'Cliente' : 'Proveedor'}</Th>
                              <Th>Número</Th>
                              <Th>Fecha</Th>
                              <Th>Vencimiento</Th>
                              <Th>Total</Th>
                              <Th>Saldo</Th>
                              <Th>Días Venc.</Th>
                              <Th>Estado</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {invoices
                              .filter(inv => inv.currency?.code === 'USD')
                              .map((invoice) => (
                                <Tr key={invoice.invoiceId}>
                                  <Td>
                                    <VStack align="start" spacing={0}>
                                      <Text fontSize="sm" fontWeight="medium" color="blue.600">
                                        {invoice.provider?.rut || invoice.client?.documentNumber}
                                      </Text>
                                      <Text fontSize="xs" color="gray.500">
                                        {invoice.provider?.name || invoice.client?.name}
                                      </Text>
                                    </VStack>
                                  </Td>
                                  <Td>{renderPurchaseInvoiceNumber(invoice)}</Td>
                                  <Td>{formatDate(invoice.date || invoice.fecha_emison)}</Td>
                                  <Td>{formatDate(invoice.expirationDate)}</Td>
                                  <Td>US$ {(invoice.totalAmount || 0).toFixed(2)}</Td>
                                  <Td fontWeight="medium" color="orange.600">
                                    US$ {(
                                      invoice.payment?.paymentAmount 
                                        ? invoice.totalAmount - Number(invoice.payment.paymentAmount) 
                                        : invoice.totalAmount
                                    ).toFixed(2)}
                                  </Td>
                                  <Td>
                                    {getDaysOverdue(invoice.expirationDate) > 0 && (
                                      <Text color="red.500" fontSize="sm" fontWeight="medium">
                                        {getDaysOverdue(invoice.expirationDate)}
                                      </Text>
                                    )}
                                  </Td>
                                  <Td>
                                    <Badge colorScheme={getStatusColor(invoice.status)}>
                                      {invoice.status}
                                    </Badge>
                                  </Td>
                                </Tr>
                              ))}
                          </Tbody>
                          <Thead>
                            <Tr bg="gray.50">
                              <Th colSpan={4}>Total en USD</Th>
                              <Th fontWeight="bold" color="green.700">
                                US$ {
                                  invoices
                                    .filter(inv => inv.currency?.code === 'USD')
                                    .reduce((sum, inv) => sum + (inv.totalAmount || 0), 0)
                                    .toFixed(2)
                                }
                              </Th>
                              <Th fontWeight="bold" color="orange.700">
                                US$ {
                                  invoices
                                    .filter(inv => inv.currency?.code === 'USD')
                                    .reduce((sum, inv) => 
                                      sum + (inv.payment?.paymentAmount 
                                        ? inv.totalAmount - Number(inv.payment.paymentAmount) 
                                        : inv.totalAmount), 0)
                                    .toFixed(2)
                                }
                              </Th>
                              <Th colSpan={2}></Th>
                            </Tr>
                          </Thead>
                        </Table>
                      </Box>
                    ) : (
                      <Text color="gray.500" textAlign="center">
                        No hay facturas pendientes en dólares
                      </Text>
                    )}
                  </VStack>
                </TabPanel>

                {/* Tab de Notas de Crédito */}
                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    <Heading size="md" color="secondaryGray.900">
                      Notas de Crédito
                    </Heading>

                    {creditNotes && creditNotes.length > 0 ? (
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>{accountType === 'receivable' ? 'Cliente' : 'Proveedor'}</Th>
                              <Th>Número</Th>
                              <Th>Fecha</Th>
                              <Th>Factura Referencia</Th>
                              <Th>Monto</Th>
                              <Th>Estado</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {creditNotes.map((creditNote) => (
                              <Tr key={creditNote.id}>
                                <Td>
                                  <VStack align="start" spacing={0}>
                                    <Text fontSize="sm" fontWeight="medium" color="blue.600">
                                      {creditNote.provider?.rut || creditNote.customer?.documentNumber}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      {creditNote.provider?.name || creditNote.customer?.name}
                                    </Text>
                                  </VStack>
                                </Td>
                                <Td fontWeight="medium">{creditNote.series}-{creditNote.number}</Td>
                                <Td>{formatDate(creditNote.issueDate)}</Td>
                                <Td>{creditNote.referenceInvoice || 'N/A'}</Td>
                                <Td fontWeight="medium" color="red.600">
                                  {formatCurrency(creditNote.invoiceTotals?.total || 0)}
                                </Td>
                                <Td>
                                  <Badge colorScheme="gray">
                                    Nota de Crédito
                                  </Badge>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    ) : (
                      <Text color="gray.500" textAlign="center">
                        No hay notas de crédito
                      </Text>
                    )}
                  </VStack>
                </TabPanel>

                {/* Tab de Historial de Pagos */}
                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    <Heading size="md" color="secondaryGray.900">
                      Historial de Pagos
                    </Heading>

                    {payments.length > 0 ? (
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>{accountType === 'receivable' ? 'Cliente' : 'Proveedor'}</Th>
                              <Th>Fecha</Th>
                              <Th>Método</Th>
                              <Th>Referencia</Th>
                              <Th>Facturas</Th>
                              <Th>Monto</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {payments.map((payment) => (
                              <Tr key={payment.id}>
                                
                                  <Td>
                                    <VStack align="start" spacing={0}>
                                      <Text fontSize="sm" fontWeight="medium" color="blue.600">
                                        {payment.provider?.rut ||
                                          payment.invoiceCustomer?.documentNumber ||
                                          payment.client?.documentNumber}
                                      </Text>
                                      <Text fontSize="xs" color="gray.500">
                                        {payment.provider?.name ||
                                          payment.invoiceCustomer?.name ||
                                          payment.client?.name}
                                      </Text>
                                    </VStack>
                                  </Td>
                                
                                <Td>{formatDate(payment.paymentDate)}</Td>
                                <Td>{payment.paymentType2?.type || '—'}</Td>
                                <Td>{payment.paymentReference || '—'}</Td>
                                <Td>{renderPaymentLinkedInvoices(payment)}</Td>
                                <Td fontWeight="medium" color="green.600">
                                  {formatCurrency(Number(payment.paymentAmount))}
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    ) : (
                      <Text color="gray.500" textAlign="center">
                        No hay historial de pagos
                      </Text>
                    )}
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </VStack>
        )}

        {!accountData && (
          <Box textAlign="center" py={8}>
            <Text color="gray.500" fontSize="lg" mb={2}>
              {selectedEntity
                ? `Ver estado de cuenta de ${selectedEntity.name}`
                : `Resumen general de cuentas por ${accountType === 'receivable' ? 'cobrar' : 'pagar'}`
              }
            </Text>
            <Text color="gray.400" fontSize="sm">
              {selectedEntity
                ? 'Haz clic en "Actualizar Estado de Cuenta" para cargar los datos'
                : 'Haz clic en "Cargar Resumen General" para ver todas las cuentas o selecciona una entidad específica'
              }
            </Text>
          </Box>
        )}
      </VStack>

      <PurchaseInvoiceDetailModal
        isOpen={Boolean(purchaseInvoiceDetailId)}
        onClose={() => setPurchaseInvoiceDetailId(null)}
        invoiceId={purchaseInvoiceDetailId}
      />
    </PageTable>
  );
}
